/**
 * CR social accounts (FR-ONB-006, 007, 008).
 *
 * The creator picks a platform, we send them to Zernio's hosted consent
 * screen, and they come back to `/social`. This page never asks for a
 * social password and never displays a token. Health states are the
 * FR-ONB-008 vocabulary; a revoked account stays on the list so
 * reconnection is a reconnect, not a disappearing row.
 */
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Eyebrow, PageHeader, StateBlock } from "@ui/primitives";
import { useToast } from "@app/shared/Toasts";
import { fontSize } from "@design/index";
import type { Tone } from "@design/index";
import { api } from "@app/shared/api";
import { CardListSkeleton } from "@app/shared/Skeletons";
import { KNOWN_PLATFORMS, PlatformIcon } from "@app/shared/Platforms";
import { connectResultFeedback } from "./socialResult";

interface Platform {
  id: string;
  label: string;
  connectable: boolean;
}

interface Connection {
  id: string;
  platform: string;
  handle: string | null;
  connection_state: string;
  connected_at: string | null;
  state_changed_at: string;
}

const STATE: Record<string, { tone: Tone; says: string }> = {
  CONNECTED: { tone: "ok", says: "Connected" },
  EXPIRING: { tone: "warn", says: "Expiring" },
  EXPIRED: { tone: "warn", says: "Expired" },
  REVOKED: { tone: "neutral", says: "Revoked" },
  INSUFFICIENT_PERMISSION: { tone: "warn", says: "Needs permission" },
  ERROR: { tone: "danger", says: "Error" },
  PENDING: { tone: "process", says: "Connecting" },
};

const RESULT: Record<string, true> = {
  linked: true,
  taken: true,
  expired: true,
  unavailable: true,
  cancelled: true,
  failed: true,
  permissions: true,
  no_channel: true,
};

const PLATFORM_LABEL: Record<string, string> = {
  youtube: "YouTube",
  tiktok: "TikTok",
  instagram: "Instagram",
  facebook: "Facebook",
  reddit: "Reddit",
};

function when(v: string | null): string {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 16).replace("T", " ");
}

export function SocialAccounts({
  embedded = false,
  onConnections,
}: {
  embedded?: boolean;
  /** How many accounts are connected or expiring, after each refresh. */
  onConnections?: (active: number) => void;
} = {}) {
  const [loaded, setLoaded] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();

  const refresh = useCallback(async (): Promise<Connection[]> => {
    const r = await api.get<{
      configured: boolean;
      platforms: Platform[];
      connections: Connection[];
      note?: string;
    }>("/social-connections");
    if (!r.ok) {
      setError(r.message);
      setLoaded(true);
      return [];
    }
    setError(null);
    setConfigured(r.data.configured);
    setPlatforms(r.data.platforms ?? []);
    setConnections(r.data.connections ?? []);
    setNote(r.data.note ?? null);
    setLoaded(true);
    return r.data.connections ?? [];
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const result = q.get("result");
    const platform = q.get("platform");
    if (result && RESULT[result]) {
      window.history.replaceState({}, "", "/social");
    }
    void (async () => {
      const rows = await refresh();
      const feedback = connectResultFeedback(result, platform, rows);
      if (feedback.kind === "ok") toast.ok(feedback.text);
      else if (feedback.kind === "error") toast.error(feedback.text);
    })();
  }, [refresh]);

  useEffect(() => {
    if (!loaded || !onConnections) return;
    const active = connections.filter((row) =>
      row.connection_state === "CONNECTED" || row.connection_state === "EXPIRING").length;
    onConnections(active);
  }, [loaded, connections, onConnections]);

  async function connect(platform: string) {
    setBusy(platform); setError(null);
    const r = await api.post<{ authUrl: string }>("/social-connections", { platform });
    setBusy(null);
    if (!r.ok) {
      setError(r.message);
      toast.error(r.message);
      return;
    }
    window.location.assign(r.data.authUrl);
  }

  async function disconnect(id: string) {
    setBusy(id); setError(null);
    const r = await api.post(`/social-connections/${id}/disconnect`);
    setBusy(null);
    if (!r.ok) { setError(r.message); toast.error(r.message); return; }
    toast.ok("Connection revoked. Collection from that account has stopped.");
    await refresh();
  }

  if (!loaded) return <CardListSkeleton count={4} />;

  return (
    <>
      {embedded ? null : (
        <PageHeader
          eyebrow="Connections"
          title="Social accounts"
          description="Connect YouTube, TikTok, Instagram, Facebook or Reddit so ClipMaster can measure posts you publish yourself. We never ask for a social password - you authorize on the platform."
        />
      )}

      {error ? <StateBlock kind="error" title="Could not update social accounts" detail={error} /> : null}

      {!configured ? (
        <StateBlock
          kind="forbidden"
          title="Social connections are not available here"
          detail="Social connections are not available on this environment."
        />
      ) : (
        <div style={{ display: "grid", gap: 20, width: "100%" }}>
          <section style={{ display: "grid", gap: 12 }}>
            <Eyebrow>Connect a platform</Eyebrow>
            <div className="cm-connect-grid" style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            }}>
              {platforms.map((p) => (
                <Card key={p.id} padding={18} style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <PlatformIcon id={p.id} size={36} title={p.label} />
                    <strong style={{ fontSize: fontSize.h4 }}>{p.label}</strong>
                  </div>
                  <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                    Authorize {p.label} on a screen they host. ClipMaster stores the
                    connection, not your login.
                  </p>
                  <div>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy !== null}
                      onClick={() => void connect(p.id)}
                    >
                      {busy === p.id ? "Redirecting…" : `Connect ${p.label}`}
                    </Button>
                  </div>
                </Card>
              ))}
              {KNOWN_PLATFORMS.filter((p) => !platforms.some((x) => x.id === p.id)).map((p) => (
                <Card key={p.id} padding={18} style={{ display: "grid", gap: 12, opacity: 0.78 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <PlatformIcon id={p.id} size={36} title={p.label} />
                    <strong style={{ fontSize: fontSize.h4 }}>{p.label}</strong>
                  </div>
                  <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                    {p.label} campaigns can ask for this network. Connect opens here when the route is live.
                  </p>
                  <div>
                    <Button size="sm" disabled>Not available yet</Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          <section style={{ display: "grid", gap: 12 }}>
            <Eyebrow>Your accounts</Eyebrow>
            {connections.length === 0 ? (
              <StateBlock
                kind="empty"
                title="No accounts connected yet"
                detail="Connect a network to start. Views stay blank until a post is measured - never shown as zero."
              />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {connections.map((row) => {
                  const st = STATE[row.connection_state] ?? {
                    tone: "neutral" as Tone, says: row.connection_state,
                  };
                  const active = row.connection_state === "CONNECTED"
                    || row.connection_state === "EXPIRING"
                    || row.connection_state === "INSUFFICIENT_PERMISSION";
                  return (
                    <Card key={row.id} padding={16} style={{
                      display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
                    }}>
                      <div style={{ flex: "1 1 220px", display: "flex", gap: 12, alignItems: "center" }}>
                        <PlatformIcon id={row.platform} size={32} />
                        <div style={{ display: "grid", gap: 4 }}>
                        <strong>
                          {PLATFORM_LABEL[row.platform] ?? row.platform}
                          {row.handle ? ` · ${row.handle}` : ""}
                        </strong>
                        <span style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                          {active
                            ? `Connected ${when(row.connected_at)}`
                            : `Last changed ${when(row.state_changed_at)}`}
                        </span>
                        </div>
                      </div>
                      <Badge tone={st.tone}>{st.says}</Badge>
                      {active ? (
                        <Button
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void disconnect(row.id)}
                        >
                          {busy === row.id ? "Revoking…" : "Disconnect"}
                        </Button>
                      ) : null}
                    </Card>
                  );
                })}
              </div>
            )}
          </section>

          {note ? (
            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)" }}>{note}</p>
          ) : null}
        </div>
      )}
    </>
  );
}
